import { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import InputDateUTC from '../../../FormControl/input/InputDateUTC';
import useFetchData from '../../../../hooks/useFetchData';
import { useDispatch, useSelector } from 'react-redux';
import { updateFilter, showFilter } from '../../../../store/actions/filter.actions';
import { useLocation, useNavigate } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import SelectMultipleField from '../../../FormControl/input/SelectMultipleField';
import { updateSkip } from '../../../../store/actions/datatable.actions';
import DialogConfirmCancel from '../../../dialog/DialogConfirm';
import SelectField from '../../../FormControl/input/SelectField';
import InputField from '../../../FormControl/input/InputField';
import AutocompleteField from '../../../FormControl/input/AutocompleteField';
import { postTypes } from '../Form/Form';

const FilterDialog = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    isLoading: isLoadingRoles,
    data: dataRoles,
    error: errorFetchRoles
  } = useFetchData('roles/standard');
  const { isLoading: isLoadingYears, data: dataYears = [] } = useFetchData('setting/year');
  const filter = useSelector((state) => state?.filter.feed?.filter || {});
  const open = useSelector((state) => state?.filter.feed?.open || false);
  const dispatch = useDispatch();
  const [form, setInputForm] = useState(filter);
  const [error, setInputError] = useState({});

  useEffect(() => {
    if (!isEmpty(filter)) {
      setInputForm(filter);
    }
  }, [filter]);

  const onClose = () => {
    dispatch(showFilter('feed', false));
  };

  const onChange = (name, value) => {
    if (name === 'rolesId') {
      setInputError({ ...error, [name]: null, roles: null });
      const roles = dataRoles.filter((item) => value.includes(item.id));
      setInputForm({ ...form, [name]: value, roles });
    } else if (name === 'yearLevelId') {
      setInputError({ ...error, [name]: null, yearLevel: null });
      const yearLevel = dataYears.filter((item) => value.includes(item._id));
      setInputForm({ ...form, [name]: value, yearLevel });
    } else if (name === 'startDatePusblishedAt' || name === 'endDatePusblishedAt') {
      const range = {
        start: name === 'startDatePusblishedAt' ? value : form.startDatePusblishedAt,
        end: name === 'endDatePusblishedAt' ? value : form.endDatePusblishedAt
      };
      setInputForm({ ...form, [name]: value, rangePublishedAt: range });
      setInputError({ ...error, [name]: null });
    } else if (name === 'startDateExpireAt' || name === 'endDateExpireAt') {
      const range = {
        start: name === 'startDateExpireAt' ? value : form.startDateExpireAt,
        end: name === 'endDateExpireAt' ? value : form.endDateExpireAt
      };
      setInputForm({ ...form, [name]: value, rangeExpireAt: range });
      setInputError({ ...error, [name]: null });
    } else {
      setInputForm({ ...form, [name]: value });
      setInputError({ ...error, [name]: null });
    }
  };

  const onApply = () => {
    onClose();
    const filter = {
      ...form
    };

    dispatch(updateFilter('feed', filter));
    dispatch(updateSkip('feed', 0));
    navigate(pathname, { state: filter });
  };

  return (
    <DialogConfirmCancel
      id='feed-filter'
      open={open}
      title='Filter'
      maxWidth='xs'
      onCancel={onClose}
      onConfirm={onApply}
      textConfirm='Apply'
    >
      <Box className='flex flex-col gap-4'>
        <div className='grid grid-cols-3 items-center gap-4'>
          <Typography
            variant='inherit'
            className='font-bold capitalize'
            color='primary'
            align='right'
            component='p'
          >
            Title
          </Typography>
          <div className='col-span-2'>
            <InputField
              id='form-filter-title'
              name='title'
              value={form.title}
              error={error.title}
              helperText={error.title}
              onChange={(e) => onChange('title', e.target.value)}
              placeholder='Enter title'
            />
          </div>
          <Typography
            variant='inherit'
            className='font-bold capitalize'
            color='primary'
            align='right'
            component='p'
          >
            Post type
          </Typography>
          <div className='col-span-2'>
            <AutocompleteField
              id={`autocomplete-post-type`}
              name={'postType'}
              placeholder='Select type'
              options={postTypes}
              error={error?.postType}
              helperText={error?.postType}
              onChange={(e) => onChange('postType', e)}
              value={form?.postType?.label || ''}
              clearOnEscape={false}
              disableClearable
            />
          </div>
          <Typography
            variant='inherit'
            className='font-bold capitalize'
            color='primary'
            align='right'
            component='p'
          >
            Roles
          </Typography>
          <div className='col-span-2'>
            <SelectMultipleField
              id='form-filter-roles'
              name='rolesId'
              value={form.rolesId}
              error={error.rolesId || errorFetchRoles}
              helperText={error.rolesId || errorFetchRoles}
              onChange={(e) => onChange('rolesId', e.target.value)}
              loading={isLoadingRoles}
              options={dataRoles?.map((item) => ({ value: item.id, label: item.name })) || []}
              placeholder='Choose a role'
            />
          </div>
          <Typography
            variant='inherit'
            className='font-bold capitalize'
            color='primary'
            align='right'
            component='p'
          >
            Year Levels
          </Typography>
          <div className='col-span-2'>
            <SelectMultipleField
              id='form-filter-year-levels'
              name='yearLevelId'
              value={form.yearLevelId}
              error={error.yearLevelId}
              helperText={error.yearLevelId}
              onChange={(e) => onChange('yearLevelId', e.target.value)}
              loading={isLoadingYears}
              options={dataYears?.map((item) => ({ value: item.name, label: item.name })) || []}
              placeholder='Choose year levels'
            />
          </div>
          <Typography
            variant='inherit'
            className='font-bold capitalize'
            color='primary'
            align='right'
            component='p'
          >
            Status
          </Typography>
          <div className='col-span-2'>
            <SelectField
              id='form-filter-status'
              name='status'
              value={form.status}
              error={error.status}
              helperText={error.status}
              onChange={(e) => onChange('status', e.target.value)}
              options={[
                { value: 'Published', label: 'Published' },
                { value: 'Scheduled', label: 'Scheduled' },
                { value: 'Expired', label: 'Expired' }
              ]}
              placeholder='Choose a option'
            />
          </div>
          <Typography
            variant='inherit'
            className='font-bold capitalize'
            color='primary'
            align='right'
            component='p'
          >
            Pusblish At
          </Typography>
          <InputDateUTC
            id='form-filter-pusblishedAt'
            name='startDatePusblishedAt'
            value={form.startDatePusblishedAt}
            error={error.startDatePusblishedAt}
            helperText={error.startDatePusblishedAt}
            onChange={(value) => onChange('startDatePusblishedAt', value)}
            clearable={true}
          />
          <InputDateUTC
            id='form-filter-pusblishedAt'
            name='endDatePusblishedAt'
            value={form.endDatePusblishedAt}
            error={error.endDatePusblishedAt}
            helperText={error.endDatePusblishedAt}
            minDate={form.startDatePusblishedAt}
            isStartOfTheDay={false}
            onChange={(value) => onChange('endDatePusblishedAt', value)}
            clearable={true}
          />
          <Typography
            variant='inherit'
            className='font-bold capitalize'
            color='primary'
            align='right'
            component='p'
          >
            Expire At
          </Typography>
          <InputDateUTC
            id='form-filter-pusblishedAt'
            name='startExpireAt'
            value={form.startDateExpireAt}
            error={error.startDateExpireAt}
            helperText={error.startDateExpireAt}
            onChange={(value) => onChange('startDateExpireAt', value)}
            clearable={true}
          />
          <InputDateUTC
            id='form-filter-pusblishedAt'
            name='endExpireAt'
            value={form.endDateExpireAt}
            error={error.endDateExpireAt}
            helperText={error.endDateExpireAt}
            minDate={form.endDateExpireAt}
            isStartOfTheDay={false}
            onChange={(value) => onChange('endDateExpireAt', value)}
            clearable={true}
          />
        </div>
      </Box>
    </DialogConfirmCancel>
  );
};

export default FilterDialog;
